import React, { ChangeEvent, HTMLProps, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { LinkExtension, ShortcutHandlerProps, createMarkPositioner } from 'remirror/extensions';

import {
  CommandButton,
  CommandButtonGroup,
  FloatingToolbar,
  FloatingWrapper,
  ListButtonGroup,
  ToggleBoldButton,
  ToggleCodeButton,
  ToggleItalicButton,
  ToggleStrikeButton,
  ToggleUnderlineButton,
  useActive,
  useAttrs,
  useChainedCommands,
  useCurrentSelection,
  useExtensionEvent,
  useUpdateReason,
} from '@remirror/react';
import HighlightButtons from '../Buttons/HighlightButtons';

const useLinkShortcut = () => {
  const [linkShortcut, setLinkShortcut] = useState<ShortcutHandlerProps | undefined>();
  const [isEditing, setIsEditing] = useState(false);

  useExtensionEvent(
    LinkExtension,
    'onShortcut',
    useCallback(
      (props) => {
        if (!isEditing) {
          setIsEditing(true);
        }

        return setLinkShortcut(props);
      },
      [isEditing],
    ),
  );

  return { linkShortcut, isEditing, setIsEditing };
};

const useFloatingLinkState = () => {
  const chain = useChainedCommands();
  const { isEditing, linkShortcut, setIsEditing } = useLinkShortcut();
  const { to, empty } = useCurrentSelection();

  const url = (useAttrs().link()?.href as string) ?? '';
  const [href, setHref] = useState<string>(url);

  // A positioner which only shows for links.
  const linkPositioner = useMemo(() => createMarkPositioner({ type: 'link' }), []);
  const onRemove = useCallback(() => chain.removeLink().focus().run(), [chain]);
  const updateReason = useUpdateReason();

  useLayoutEffect(() => {
    if (!isEditing) {
      return;
    }

    if (updateReason.doc || updateReason.selection) {
      setIsEditing(false);
    }
  }, [isEditing, setIsEditing, updateReason.doc, updateReason.selection]);

  useEffect(() => {
    setHref(url);
  }, [url]);

  const submitHref = useCallback(() => {
    setIsEditing(false);
    const range = linkShortcut ?? undefined;

    if (href === '') {
      chain.removeLink();
    } else {
      chain.updateLink({ href, auto: false }, range);
    }

    chain.focus(range?.to ?? to).run();
  }, [setIsEditing, linkShortcut, chain, href, to]);

  const cancelHref = useCallback(() => {
    setIsEditing(false);
  }, [setIsEditing]);

  const clickEdit = useCallback(() => {
    if (empty) {
      chain.selectLink();
    }

    setIsEditing(true);
  }, [chain, empty, setIsEditing]);

  return useMemo(
    () => ({
      href,
      setHref,
      linkShortcut,
      linkPositioner,
      isEditing,
      clickEdit,
      onRemove,
      submitHref,
      cancelHref,
    }),
    [href, linkShortcut, linkPositioner, isEditing, clickEdit, onRemove, submitHref, cancelHref],
  );
};

const DelayAutoFocusInput = ({ autoFocus, ...rest }: HTMLProps<HTMLInputElement>) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!autoFocus) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [autoFocus]);

  return <input ref={inputRef} {...rest} />;
};

const CustomFloatingToolbar = () => {
  const { isEditing, linkPositioner, clickEdit, onRemove, submitHref, href, setHref, cancelHref } = useFloatingLinkState();
  const active = useActive();
  const activeLink = active.link();
  const { empty } = useCurrentSelection();

  const handleClickEdit = useCallback(() => {
    clickEdit();
  }, [clickEdit]);

  const linkEditButtons = activeLink ? (
    <>
      <CommandButton commandName="updateLink" onSelect={handleClickEdit} icon="pencilLine" enabled />
      <CommandButton commandName="removeLink" onSelect={onRemove} icon="linkUnlink" enabled />
    </>
  ) : (
    <CommandButtonGroup>
      <ToggleBoldButton label={'볼드'} />
      <ToggleItalicButton label={'이탤릭'} />
      <ToggleUnderlineButton label={'밑줄'} />
      <ToggleStrikeButton label={'취소선'} />
      <CommandButton commandName="updateLink" onSelect={handleClickEdit} icon="link" enabled label={'링크'} />
      <HighlightButtons />
      <ToggleCodeButton label={'코드'} />
    </CommandButtonGroup>
  );

  return (
    <>
      {!isEditing && (
        <FloatingToolbar className="memo-floating-toolbar" sx={{ zIndex: 9999 }}>
          {linkEditButtons}
        </FloatingToolbar>
      )}
      {!isEditing && empty && (
        <FloatingToolbar className="memo-floating-toolbar" positioner={linkPositioner} sx={{ zIndex: 9999 }}>
          {linkEditButtons}
        </FloatingToolbar>
      )}

      <FloatingWrapper positioner="always" placement="bottom-end" enabled={isEditing}>
        <DelayAutoFocusInput
          style={{ zIndex: 9999 }}
          placeholder="링크 추가"
          autoFocus
          onChange={(event: ChangeEvent<HTMLInputElement>) => setHref(event.target.value)}
          value={href}
          onKeyDown={(event) => {
            const { code } = event;

            if (code === 'Enter') {
              submitHref();
            }

            if (code === 'Escape') {
              cancelHref();
            }
          }}
        />
      </FloatingWrapper>
    </>
  );
};

export default CustomFloatingToolbar;
